import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { IconButton, Stack, Typography, styled } from "@mui/material";

/**
 * The ± control used both in the item drawer's footer and on every
 * quantity-group option row.
 *
 * `setQuantity(value, isAbsolute)` — the buttons send −1/+1 as a DELTA; typing
 * in the input sends the value itself with `isAbsolute`. Keeping one callback
 * for both is what lets the caller clamp once.
 *
 * `minClickable` is the subtle one, and it is load-bearing on option rows: at
 * the minimum the minus button stays *enabled* but drops to 0.38 opacity, and
 * pressing it drives the quantity below the minimum — which the engine reads as
 * "deselect this option". Disabling it instead would leave a chosen option with
 * no way to un-choose it.
 *
 * The row stops propagation so ± never toggles the option it sits on.
 */

const Root = styled(Stack)({
  flexDirection: "row",
  alignItems: "center",
});

const Value = styled("input")({
  border: "none",
  outline: "none",
  background: "transparent",
  textAlign: "center",
  fontSize: "1.2rem",
  fontWeight: 700,
  color: "inherit",
  padding: 0,
  transition: "width .25s",
});

interface QuantityStepperProps {
  quantity: number;
  setQuantity: (value: number, isAbsolute?: boolean) => void;
  minQuantity?: number;
  maxQuantity?: number;
  /** Keep minus enabled at the minimum, dimmed — see above. */
  minClickable?: boolean;
  /** Footer instance renders a plain Typography instead of an input. */
  showInput?: boolean;
}

export default function QuantityStepper({
  quantity,
  setQuantity,
  minQuantity = 1,
  maxQuantity = Infinity,
  minClickable = false,
  showInput = false,
}: QuantityStepperProps) {
  const atMin = quantity <= minQuantity;
  const atMax = quantity >= maxQuantity;

  return (
    <Root
      onClick={(event) => {
        event.stopPropagation();
      }}
    >
      <IconButton
        aria-label="remove"
        size="small"
        disabled={atMin && !minClickable}
        onClick={() => setQuantity(-1)}
        sx={
          atMin && minClickable
            ? { opacity: 0.38, pointerEvents: "auto" }
            : undefined
        }
      >
        <RemoveIcon fontSize="small" />
      </IconButton>

      {showInput ? (
        <Value
          type="tel"
          value={quantity}
          onFocus={(event) => event.currentTarget.select()}
          onChange={(event) => {
            const next = Number(event.target.value.replace(/\D/g, ""));
            if (!Number.isNaN(next)) setQuantity(next, true);
          }}
          // Grows with the number rather than reserving room for four digits.
          style={{ width: Math.max(15, 14 * String(quantity).length) }}
        />
      ) : (
        <Typography sx={{ fontSize: "1.2rem", fontWeight: 700, px: 1 }}>
          {quantity}
        </Typography>
      )}

      <IconButton
        aria-label="add"
        size="small"
        disabled={atMax}
        onClick={() => setQuantity(1)}
      >
        <AddIcon fontSize="small" />
      </IconButton>
    </Root>
  );
}
